import type * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import debounce from 'lodash/debounce'

import { ChevronDownIcon, Popover } from '@buffer-mono/popcorn'

import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import { ThreadsLocationProductDiscoveryNotification } from './ThreadsLocationProductDiscoveryNotification'
import ComposerActionCreators from '../../action-creators/ComposerActionCreators'
import AppStore from '../../stores/AppStore'
import { useSaveCallbacks } from '../../utils/useSaveCallbacks'
import { FieldWrapper, Label } from '../channel-fields/styles'
import styles from '../location-shared/LocationDropdown.module.css'
import {
  type ThreadsLocation,
  useSearchThreadsLocations,
} from './hooks/useSearchThreadsLocations'
import { ThreadsLocationRefreshCoachmark } from './ThreadsLocationRefreshCoachmark'

interface ThreadsLocationComposerBarProps {
  draft: Draft
}

const getSelectedThreadsChannelIds = (): string[] => {
  return AppStore.getSelectedProfiles()
    .filter((profile) => profile.service.name === 'threads')
    .map((profile) => profile.id)
}

const isRefreshRequired = (): boolean => {
  return AppStore.getSelectedProfiles().some(
    (profile) =>
      profile.service.name === 'threads' && !profile.canAssociateLocation,
  )
}

const ThreadsLocationComposerBar: React.FC<ThreadsLocationComposerBarProps> = ({
  draft,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] =
    useState<ThreadsLocation | null>(null)
  const [showCoachmark, setShowCoachmark] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const channelIds = getSelectedThreadsChannelIds()
  const needsRefresh = isRefreshRequired()
  const { saveAsDraft } = useSaveCallbacks()

  const { searchLocations, locations, loading, errorMessage } =
    useSearchThreadsLocations()

  useEffect(() => {
    const locationId = draft?.channelData?.threads?.location_id ?? null
    const locationName = draft?.channelData?.threads?.location_name ?? ''
    if (locationId && locationName) {
      setSelectedLocation({ id: locationId, name: locationName })
      setInputValue(locationName)
    }
  }, [draft])

  const loadOptions = useCallback(
    async (input: string) => {
      if (!input || channelIds.length === 0) {
        return
      }

      await searchLocations(input, channelIds)
    },
    [channelIds, searchLocations],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLoadOptions = useCallback(
    debounce(async (input: string) => {
      await loadOptions(input)
      setIsOpen(true)
    }, 300),
    [loadOptions],
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value
    setInputValue(value)
    setSelectedLocation(null)

    if (needsRefresh && value.trim().length > 0) {
      setShowCoachmark(true)
      setIsOpen(false)
      return
    }

    if (value.trim().length >= 2) {
      debouncedLoadOptions(value)?.catch(() => {
        // do nothing
      })
    } else {
      setIsOpen(false)
    }
  }

  const handleInputFocus = (): void => {
    if (needsRefresh) {
      setShowCoachmark(true)
    }
  }

  const handleOptionClick = (option: ThreadsLocation): void => {
    setSelectedLocation(option)
    setInputValue(option.name)
    setIsOpen(false)
    inputRef.current?.focus()
    ComposerActionCreators.updateDraftThreadsData({
      ...draft.channelData?.threads,
      location_id: option.id,
      location_name: option.name,
    })
  }

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        if (selectedLocation) {
          setInputValue(selectedLocation.name)
        } else {
          setInputValue('')
        }
      }
    },
    [selectedLocation],
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  return (
    <FieldWrapper>
      <Label htmlFor="threads-location">Location</Label>
      <div style={{ flex: 1 }}>
        <ThreadsLocationProductDiscoveryNotification
          visible={!needsRefresh && !showCoachmark}
        >
          <ThreadsLocationRefreshCoachmark
            open={showCoachmark}
            onDismiss={(): void => setShowCoachmark(false)}
            channelIds={channelIds}
            saveAsDraft={saveAsDraft}
          >
            <Popover open={isOpen || loading}>
              <Popover.Anchor>
                <input
                  id="threads-location"
                  className={styles.input}
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onKeyDown={(event): void => {
                    if (event.key === 'Escape') {
                      event.stopPropagation()
                      setIsOpen(false)
                    }

                    if (event.key === 'Tab') {
                      dropdownRef.current?.focus()
                    }
                  }}
                  placeholder="Type the location"
                />
              </Popover.Anchor>
              <ChevronDownIcon className={styles.chevronIcon} />
              {(isOpen || loading) && (
                <Popover.Content
                  onOpenAutoFocus={(event): void => {
                    event.preventDefault()
                  }}
                  className={styles.locationDropdown}
                  ref={dropdownRef}
                  onKeyDown={(event): void => {
                    if (event.key === 'Escape') {
                      event.stopPropagation()
                      setIsOpen(false)
                      inputRef.current?.focus()
                    }
                  }}
                >
                  {errorMessage && (
                    <div className={styles.emptyContainer}>
                      <span className={styles.emptyText}>{errorMessage}</span>
                    </div>
                  )}
                  {!errorMessage && loading ? (
                    <div className={styles.loadingContainer}>
                      <div className={styles.loadingSpinner} />
                      <span className={styles.loadingText}>
                        Searching locations...
                      </span>
                    </div>
                  ) : !errorMessage && locations.length > 0 ? (
                    <div className={styles.optionsContainer}>
                      {locations.map((option) => (
                        <button
                          className={styles.locationOption}
                          key={option.id}
                          onClick={(): void => handleOptionClick(option)}
                        >
                          <div className={styles.locationName}>
                            {option.name}
                          </div>
                          {option.address && (
                            <div className={styles.locationDetails}>
                              {option.address}
                              {option.city && `, ${option.city}`}
                              {option.country && `, ${option.country}`}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    !errorMessage && (
                      <div className={styles.emptyContainer}>
                        <span className={styles.emptyText}>
                          No locations found
                        </span>
                      </div>
                    )
                  )}
                </Popover.Content>
              )}
            </Popover>
          </ThreadsLocationRefreshCoachmark>
        </ThreadsLocationProductDiscoveryNotification>
      </div>
    </FieldWrapper>
  )
}

export default ThreadsLocationComposerBar
